import {GOOGLE_PICKER_API_KEY, GOOGLE_PICKER_APP_ID} from "../config";

/**
 * Google Picker, the file picker for Google Drive media sources.
 *
 * The Picker runs with a short-lived token of the chosen Drive account (from the cabinet) and only
 * returns what the user picks; with the drive.file scope that is also all the server can read.
 */

/** What the cabinet's import endpoint takes, per picked file. */
export interface PickedMediaSourceFile {
    id: string;
    name: string;
    mimeType: string;
    sizeBytes?: number;
    resourceKey?: string;
}

const GAPI_SRC = "https://apis.google.com/js/api.js";

/** The types the post editor accepts, by extension, as MIME types the Picker can filter on. */
const MIME_BY_EXTENSION: {[ext: string]: string} = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    mp4: "video/mp4",
    mov: "video/quicktime",
    avi: "video/x-msvideo",
};

export const isGooglePickerConfigured = (): boolean => GOOGLE_PICKER_API_KEY !== '' && GOOGLE_PICKER_APP_ID !== '';

/**
 * MIME types for an InputFile `acceptedFiles` string (".jpg, .png, .mp4"); all supported ones when empty.
 */
export const mimeTypesForAcceptedFiles = (acceptedFiles?: string): string[] => {
    const extensions = (acceptedFiles || '')
        .split(',')
        .map(ext => ext.trim().replace(/^\./, '').toLowerCase())
        .filter(ext => ext !== '');

    const types = (extensions.length > 0 ? extensions : Object.keys(MIME_BY_EXTENSION))
        .map(ext => MIME_BY_EXTENSION[ext])
        .filter((type): type is string => type !== undefined);

    return Array.from(new Set(types));
};

/** The Picker's documents, as the import endpoint takes them. */
export const toPickedFiles = (docs: any[]): PickedMediaSourceFile[] => {
    return (docs || [])
        .filter(doc => typeof doc?.id === 'string' && doc.id !== '')
        .map(doc => {
            const file: PickedMediaSourceFile = {
                id: doc.id,
                name: typeof doc.name === 'string' && doc.name !== '' ? doc.name : doc.id,
                mimeType: typeof doc.mimeType === 'string' ? doc.mimeType : '',
            };
            const size = Number(doc.sizeBytes);
            if (Number.isFinite(size) && size > 0) {
                file.sizeBytes = size;
            }
            if (typeof doc.resourceKey === 'string' && doc.resourceKey !== '') {
                file.resourceKey = doc.resourceKey;
            }
            return file;
        });
};

let pickerLoaded: Promise<void> | null = null;

const loadPicker = (): Promise<void> => {
    if (pickerLoaded === null) {
        pickerLoaded = new Promise<void>((resolve, reject) => {
            const onApi = () => (window as any).gapi.load('picker', {callback: () => resolve(), onerror: () => reject(new Error('picker'))});

            if ((window as any).gapi?.load) {
                onApi();
                return;
            }

            const script = document.createElement('script');
            script.src = GAPI_SRC;
            script.async = true;
            script.onload = onApi;
            script.onerror = () => reject(new Error('gapi'));
            document.body.appendChild(script);
        }).catch(e => {
            pickerLoaded = null; // allow a retry
            throw e;
        });
    }

    return pickerLoaded;
};

/**
 * Open the Picker; resolves with the picked files, or an empty list when the user cancels.
 */
export const openGooglePicker = async (accessToken: string, mimeTypes: string[], isMultiple: boolean, locale?: string): Promise<PickedMediaSourceFile[]> => {
    await loadPicker();

    const google = (window as any).google;
    const mimes = mimeTypes.join(',');

    const myDrive = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes(mimes);
    const sharedDrives = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setEnableDrives(true)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes(mimes);

    return new Promise(resolve => {
        let builder = new google.picker.PickerBuilder()
            .addView(myDrive)
            .addView(sharedDrives)
            .enableFeature(google.picker.Feature.SUPPORT_DRIVES)
            .setOAuthToken(accessToken)
            .setDeveloperKey(GOOGLE_PICKER_API_KEY)
            .setAppId(GOOGLE_PICKER_APP_ID)
            .setCallback((data: any) => {
                if (data.action === google.picker.Action.PICKED) {
                    resolve(toPickedFiles(data.docs));
                } else if (data.action === google.picker.Action.CANCEL) {
                    resolve([]);
                }
            });

        if (isMultiple) {
            builder = builder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
        }
        if (locale) {
            builder = builder.setLocale(locale);
        }

        builder.build().setVisible(true);
    });
};
